import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, List, Typography, Popconfirm, message, Upload, Progress, Tree, Input } from 'antd';
import { DeleteOutlined, UploadOutlined, EyeOutlined, EyeInvisibleOutlined, LoadingOutlined, ApartmentOutlined, SearchOutlined, LockOutlined, UnlockOutlined, FileImageOutlined, FolderOpenOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import useSceneStore from '../../stores/sceneStore';
import useSelectionStore from '../../stores/selectionStore';
import { uploadModel as apiUploadModel, updateModel as apiUpdateModel, deleteModel as apiDeleteModel, uploadTextures as apiUploadTextures } from '../../api/models';
import { fetchImportPolicy, formatBytes, type ImportPolicyInfo } from '../../api/imports';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import { cameraControlsRegistry } from '../../stores/cameraControlsStore';
import { useSubObjectMenuStore } from '../../stores/subObjectMenuStore';
import useEditorUiStore from '../../stores/editorUiStore';
import type { SubObjectNode } from './ModelViewer';

const { Text } = Typography;

// 构件树 → antd Tree 数据（uuid 作 key，name 显示，搜索命中高亮）
function toTreeData(nodes: SubObjectNode[], searchText = '', onDblClick?: (uuid: string) => void): any[] {
  return nodes.map((n) => ({
    // 2026-09-14（阶段 A3）：双击节点 = 聚焦该部件（对齐 Unity 双击聚焦）
    title: onDblClick ? (
      <span onDoubleClick={(e) => { e.stopPropagation(); onDblClick(n.uuid); }}>
        {highlightName(n.name, searchText)}
      </span>
    ) : highlightName(n.name, searchText),
    key: n.uuid,
    children: n.children.length > 0 ? toTreeData(n.children, searchText, onDblClick) : undefined,
  }));
}
// 搜索词高亮（前后文拼接，命中段蓝色）
function highlightName(name: string, searchText: string) {
  if (!searchText.trim()) return name;
  const idx = name.indexOf(searchText);
  if (idx < 0) return name;
  return (
    <span>
      {name.slice(0, idx)}
      <span style={{ color: '#1677ff', background: 'rgba(22,119,255,0.15)', borderRadius: 2 }}>{name.slice(idx, idx + searchText.length)}</span>
      {name.slice(idx + searchText.length)}
    </span>
  );
}
function collectNodes(nodes: SubObjectNode[], out: SubObjectNode[] = []): SubObjectNode[] {
  nodes.forEach((n) => { out.push(n); collectNodes(n.children, out); });
  return out;
}
function topLevelKeys(nodes: SubObjectNode[]): React.Key[] {
  return nodes.map((n) => n.uuid);
}

export default function ModelPanel() {
  const { models, selectedModelId, selectModel, removeModel, updateModel, currentScene, subObjectsMap, clearModelSubObjects, subObjectUnlocked, expandedModelId, setExpandedModelId } =
    useSceneStore();
  const [uploading, setUploading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState(0);
  const [uploadFileName, setUploadFileName] = useState('');
  // 2026-10-19（M1）：权属声明 + 导入配额（服务端权威；勾选持久化在本机）
  const [rightsDeclared, setRightsDeclared] = useState<boolean>(() => {
    try { return localStorage.getItem('dtwin_import_rights_v1') === '1'; } catch { return false; }
  });
  const [importPolicy, setImportPolicy] = useState<ImportPolicyInfo | null>(null);
  const refreshImportPolicy = () => {
    fetchImportPolicy().then(setImportPolicy).catch(() => {});
  };
  useEffect(() => { refreshImportPolicy(); }, []);
  const setRights = (v: boolean) => {
    setRightsDeclared(v);
    try { localStorage.setItem('dtwin_import_rights_v1', v ? '1' : '0'); } catch { /* 忽略 */ }
  };
  // 2026-08-31｜组合模型自动解析：展开查看构件树（勾选显隐）
  // 2026-09-08｜阶段2：按需展开——默认只展开第一层，expandedKeys 管理；搜索命中自动展开父链定位
  // 2026-09-27：expandedModelId 提升到 sceneStore（刷新按场景持久化恢复展开态）
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<React.Key[]>([]);
  const [treeSearchText, setTreeSearchText] = useState('');
  const [treeSelectedKeys, setTreeSelectedKeys] = useState<React.Key[]>([]);
  // 2026-09-14（阶段 A）：树滚动定位（3D 选中部件 → 树滚动到可见，对标 Blender"显示活动"）
  const treeRef = useRef<any>(null);
  // 2026-09-14：树刷新键——部件软删除/恢复（subObjectConfigs 变化）后强制重挂 Tree，勾选状态与 3D 同步
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);
  const subObjectConfigsAll = useSceneStore((s) => s.subObjectConfigs);
  useEffect(() => {
    if (!expandedModelId) return;
    setTreeRefreshKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subObjectConfigsAll]);
  // 2026-09-27：刷新恢复展开态时构件树数据可能尚未解析完成——数据就绪后补展开第一层
  //（仅对恢复的模型补一次；用户手动展开时 openModelTree 已填 keys，prev 非空不覆盖）
  const autoExpandedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!expandedModelId || autoExpandedRef.current === expandedModelId) return;
    const tree = subObjectsMap[expandedModelId];
    if (tree && tree.length > 0) {
      autoExpandedRef.current = expandedModelId;
      setTreeExpandedKeys((prev) => (prev.length > 0 ? prev : topLevelKeys(tree)));
    }
  }, [expandedModelId, subObjectsMap]);
  // 2026-09-14：贴图（maps）导入——多文件合并队列（300ms 防抖合并为一次请求）+ 上传后自动重载模型
  const [uploadingTextures, setUploadingTextures] = useState(false);
  const texQueueRef = useRef<File[]>([]);
  const texTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openModelTree = (modelId: string) => {
    const next = expandedModelId === modelId ? null : modelId;
    setExpandedModelId(next);
    setTreeSearchText('');
    setTreeExpandedKeys(next ? topLevelKeys(subObjectsMap[next] || []) : []);
  };

  const handleTreeSearch = (text: string, modelId: string) => {
    setTreeSearchText(text);
    const tree = subObjectsMap[modelId] || [];
    if (!text.trim()) {
      setTreeExpandedKeys(topLevelKeys(tree));
      return;
    }
    const hitIds = new Set<string>();
    collectNodes(tree).forEach((n) => {
      if (n.name.includes(text.trim())) {
        useSceneStore.getState().getAncestorIds(modelId, n.uuid).forEach((id) => hitIds.add(id));
      }
    });
    setTreeExpandedKeys([...hitIds]);
  };

  // 2026-09-08｜阶段3联动：3D 点击选中子构件 → 自动展开该模型树 + 展开整条父链定位 + 树节点高亮
  // 2026-09-14：支持多选（selectedSubObjects——快速选中同源部件），主选中=数组末位
  const selectedSubObjects = useSceneStore((s) => s.selectedSubObjects);
  useEffect(() => {
    if (!selectedSubObjects || selectedSubObjects.length === 0) {
      setTreeSelectedKeys([]);
      return;
    }
    const modelId = selectedSubObjects[0].modelId;
    const uuids = selectedSubObjects.filter((x) => x.modelId === modelId).map((x) => x.uuid);
    setExpandedModelId(modelId);
    setTreeSearchText('');
    // 展开全部选中项的父链
    const exp = new Set<React.Key>();
    uuids.forEach((u) => {
      useSceneStore.getState().getAncestorIds(modelId, u).forEach((id) => exp.add(id));
    });
    setTreeExpandedKeys([...exp]);
    setTreeSelectedKeys(uuids);
    // 2026-09-14（阶段 A）：3D 选中部件 → 左侧自动切「模型」页签 + 树滚动定位到主选中部件
    useEditorUiStore.getState().setActivePanel('models');
    const primary = uuids[uuids.length - 1];
    // 2026-09-14 修复：Tree 展开渲染/重挂时序不定 —— 多次尝试滚动定位（失败打日志便于排查）
    [150, 500, 1200].forEach((delay) => {
      setTimeout(() => {
        try {
          treeRef.current?.scrollTo?.({ key: primary, align: 'auto' });
        } catch (err) {
          if (delay === 1200) console.warn('[ModelPanel] tree scrollTo failed:', err);
        }
      }, delay);
    });
  }, [selectedSubObjects]);

  // 2026-09-08｜修复：树数据 useMemo 缓存（点击节点/选中变化时避免每次渲染递归重建整棵构件树）
  const expandedTree = useMemo(() => {
    if (!expandedModelId) return null;
    const tree = subObjectsMap[expandedModelId] || [];
    return {
      // 2026-09-14：勾选状态按构件配置 visible 计算（软删除/隐藏的部件显示为未勾选，保持与 3D 一致）
      treeData: toTreeData(tree, treeSearchText, (uuid) => {
        // 双击树节点 → 选中该部件并聚焦（锁定态不允许）
        if (!useSceneStore.getState().subObjectUnlocked[expandedModelId] ) return;
        selectModel(expandedModelId);
        useSceneStore.getState().selectSubObject({ modelId: expandedModelId, uuid });
        setTimeout(() => cameraControlsRegistry.focusSelected?.(), 80);
      }),
      checkedKeys: (() => {
        const configs = useSceneStore.getState().subObjectConfigs[expandedModelId] || [];
        const hidden = new Set(configs.filter((c) => c.visible === false).map((c) => c.uuid));
        return collectNodes(tree).filter((n) => !hidden.has(n.uuid)).map((n) => n.uuid);
      })(),
    };
  }, [expandedModelId, treeSearchText, subObjectsMap, treeRefreshKey]);

  const applyVisibility = (modelId: string, checked: any) => {
    const handle = modelRefsRegistry.get(modelId);
    const tree = subObjectsMap[modelId];
    if (!handle?.setSubObjectVisibleByUuid || !tree) return;
    const checkedSet = new Set<string>(Array.isArray(checked) ? checked : checked?.checked || []);
    // 2026-09-08｜阶段5：显隐按 uuid 定位（修复同名父子构件勾选连累整组隐藏）+ 写入构件配置
    collectNodes(tree).forEach((n) => {
      handle.setSubObjectVisibleByUuid!(n.uuid, checkedSet.has(n.uuid));
      useSceneStore.getState().updateSubObjectConfig(modelId, { uuid: n.uuid, name: n.name, visible: checkedSet.has(n.uuid) });
    });
  };

  /** 2026-09-14：贴图上传（多文件防抖合并）——上传到服务器 uploads 根目录，按文件名匹配 FBX 外链引用 */
  const handleTextureUpload = (file: File) => {
    texQueueRef.current.push(file);
    if (texTimerRef.current) clearTimeout(texTimerRef.current);
    texTimerRef.current = setTimeout(async () => {
      const files = texQueueRef.current.splice(0);
      if (files.length === 0) return;
      setUploadingTextures(true);
      try {
        const res = await apiUploadTextures(files);
        message.success(`已上传 ${res?.count ?? files.length} 张贴图，模型自动重载中…`);
        refreshImportPolicy(); // M1：贴图也计入导入配额
        // 重载全部模型：清 loader 缓存 → FBX 重新解析并按文件名请求贴图
        useSceneStore.getState().models.forEach((m) => useSceneStore.getState().bumpModelReload(m.id));
      } catch (err: any) {
        const detail = err?.response?.data?.message || err?.message || '未知错误';
        message.error(`贴图上传失败：${detail}`);
      } finally {
        setUploadingTextures(false);
      }
    }, 300);
    return false;
  };

  const handleFileUpload = async (file: File) => {
    if (!currentScene) {
      message.error('请先创建场景');
      return false;
    }
    // M1：权属声明（服务端强校验；前端先拦一次给出明确指引）
    if (!rightsDeclared) {
      message.warning('请先勾选权属声明（确认拥有上传内容的合法权利），再导入模型');
      return false;
    }
    setUploading(true);
    setUploadPercent(0);
    setUploadFileName(file.name);
    try {
      const model = await apiUploadModel(currentScene.id, file, (percent) => {
        setUploadPercent(percent);
      }, { rightsDeclared: true });
      console.log('[Upload] Server response:', model);
      addModelFromApi(model as any);
      // 2026-09-14：新导入模型标记待归一化（仅新上传的模型在首次加载后做尺寸归一化；已保存模型不受影响）
      useSceneStore.getState().markNormalizePending((model as any).id);
      // 2026-09-14 修复（用户反馈"导入后镜头被拉很远"）：导入模型**不再自动 fit/移动相机**——
      // 主流编辑器（Unity/Blender）导入资产均保持当前视图不变，用户再按意图调整（双击/聚焦按钮可手动聚焦）。
      message.success(`已上传: ${file.name}`);
    } catch (err: any) {
      console.error('[Upload] Failed:', err);
      const resp = err?.response?.data || {};
      const code = resp.code || resp?.message?.code;
      const detail = resp.message || err?.message || '未知错误';
      // M1：配额/权属类错误给出可操作提示
      if (code === 'IMPORT_QUOTA_EXCEEDED') message.error(`导入配额已用完：${detail}`);
      else if (code === 'IMPORT_FILE_TOO_LARGE') message.error(`文件过大：${detail}`);
      else if (code === 'IMPORT_RIGHTS_REQUIRED') message.error(detail);
      else message.error(`上传失败: ${detail}`);
    } finally {
      setUploading(false);
      setUploadPercent(0);
      setUploadFileName('');
      refreshImportPolicy(); // 刷新占用（成功/失败都刷新，失败释放已落盘文件不计入）
    }
    return false;
  };

  const addModelFromApi = (model: any) => {
    useSceneStore.getState().addModel({
      id: model.id,
      name: model.name,
      url: model.url,
      format: model.format,
      position: model.position,
      rotation: model.rotation,
      scale: model.scale,
      visible: model.visible,
    });
  };

  const handleDelete = async (id: string) => {
    removeModel(id);
    clearModelSubObjects(id);
    useSelectionStore.getState().deselect(id);
    message.success('已删除');
    try {
      await apiDeleteModel(id);
    } catch {
      // API failed but already removed locally
    }
  };

  const handleToggleVisible = async (id: string, visible: boolean) => {
    updateModel(id, { visible });
    apiUpdateModel(id, { visible }).catch(() => {});
  };

  return (
    <div style={{ paddingTop: 12 }}>
      {uploading && (
        <div style={{
          marginBottom: 12,
          padding: '12px 16px',
          background: '#1a1a2e',
          borderRadius: 8,
          border: '1px solid #30475e',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <LoadingOutlined style={{ color: '#1677ff', fontSize: 14 }} />
            <Text style={{ color: '#ccc', fontSize: 12 }} ellipsis>
              正在上传: {uploadFileName}
            </Text>
          </div>
          <Progress
            percent={uploadPercent}
            size="small"
            strokeColor="#1677ff"
            railColor="#30475e"
            format={(pct) => <span style={{ color: '#999', fontSize: 11 }}>{pct}%</span>}
          />
        </div>
      )}

      {/* 2026-10-19（M1）：权属声明 + 导入配额（服务端权威校验；此处仅提示与引导） */}
      <div style={{ marginBottom: 8, padding: '6px 8px', background: '#14141f', border: '1px solid #30475e', borderRadius: 6 }}>
        <Checkbox
          checked={rightsDeclared}
          onChange={(e) => setRights(e.target.checked)}
          data-testid="import-rights-declare"
          style={{ fontSize: 12, color: '#bbb' }}
        >
          <SafetyCertificateOutlined style={{ color: rightsDeclared ? '#52c41a' : '#faad14' }} /> 我确认拥有上传内容的合法权利，不侵犯第三方知识产权
        </Checkbox>
        {importPolicy && (
          <div data-testid="import-quota-text" style={{ marginTop: 4, fontSize: 11, color: '#888' }}>
            导入配额（{importPolicy.tier === 'superadmin' ? '超管' : importPolicy.tier === 'paid' ? '付费' : '免费'}档）：
            {importPolicy.maxFiles < 0 ? '文件数不限' : `已用 ${importPolicy.usedFiles}/${importPolicy.maxFiles} 个`}
            {' · '}
            {importPolicy.maxTotalMB < 0 ? '容量不限' : `已用 ${formatBytes(importPolicy.usedBytes)}/${formatBytes(importPolicy.maxTotalMB * 1024 * 1024)}`}
            {importPolicy.storageBonusGB > 0 ? `（含加购 ${importPolicy.storageBonusGB}GB）` : ''}
          </div>
        )}
      </div>

      <Upload
        capture={undefined}
        accept=".glb,.gltf,.fbx,.obj,.ply,.stl"
        showUploadList={false}
        beforeUpload={handleFileUpload}
        multiple
        disabled={uploading || !rightsDeclared}
      >
        <Button
          type="dashed"
          icon={uploading ? <LoadingOutlined /> : <UploadOutlined />}
          block
          disabled={uploading || !rightsDeclared}
          title={!rightsDeclared ? '请先勾选上方权属声明' : undefined}
        >
          {uploading ? '上传中...' : rightsDeclared ? '导入 3D 模型' : '请先勾选权属声明'}
        </Button>
      </Upload>

      <Upload
        multiple
        accept=".jpg,.jpeg,.png,.tga,.bmp,.tif,.tiff,.webp"
        showUploadList={false}
        beforeUpload={handleTextureUpload}
        disabled={uploadingTextures}
      >
        <Button
          type="dashed"
          icon={uploadingTextures ? <LoadingOutlined /> : <FileImageOutlined />}
          block
          style={{ marginTop: 8 }}
          disabled={uploadingTextures}
          title="FBX 外链贴图（maps 目录）：按文件名自动匹配，上传后模型自动重载"
        >
          {uploadingTextures ? '贴图上传中...' : '导入贴图（maps，自动匹配 FBX）'}
        </Button>
      </Upload>

      <Upload
        directory
        multiple
        showUploadList={false}
        beforeUpload={handleTextureUpload}
        disabled={uploadingTextures}
      >
        <Button
          type="dashed"
          icon={uploadingTextures ? <LoadingOutlined /> : <FolderOpenOutlined />}
          block
          style={{ marginTop: 8 }}
          disabled={uploadingTextures}
          title="选择贴图文件夹（如 FBX 配套的 maps 目录）：自动导入其中所有图片并按文件名匹配"
        >
          {uploadingTextures ? '贴图上传中...' : '导入整个贴图文件夹（如 maps）'}
        </Button>
      </Upload>

      <div style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
        支持 GLB/GLTF/FBX/OBJ/PLY/STL；FBX 外链贴图可用上方两个按钮（选文件/选文件夹）一并上传
      </div>

      <List
        style={{ marginTop: 12 }}
        dataSource={models}
        locale={{ emptyText: '暂无模型，点击上方导入' }}
        renderItem={(model) => {
          const subCount = (subObjectsMap[model.id] || []).length;
          // 2026-09-14：组合模型默认锁定（整体操作）；解锁后可展开构件树/选子构件编辑
          const unlocked = subObjectUnlocked[model.id] === true;
          return (
            <div key={model.id} style={{ marginBottom: 4 }}>
              <List.Item
                onClick={() => { selectModel(model.id); if (unlocked) openModelTree(model.id); }}
                style={{
                  cursor: 'pointer',
                  // 2026-09-14 修复：选中底色原为浅蓝 #e6f4ff（深色主题下白字看不清）→ 深色高亮 + 左缘色条
                  background: selectedModelId === model.id ? 'rgba(24,144,255,0.22)' : undefined,
                  borderLeft: selectedModelId === model.id ? '2px solid #1890ff' : '2px solid transparent',
                  padding: '8px 12px',
                  borderRadius: 6,
                }}
                actions={[
                  subCount > 0 ? (
                    <Button
                      type="text" size="small"
                      icon={unlocked ? <UnlockOutlined /> : <LockOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        const next = !unlocked;
                        useSceneStore.getState().setSubObjectUnlocked(model.id, next);
                        if (!next) {
                          // 锁定即收起构件树并清除子构件选中（回到整体操作）
                          setExpandedModelId(null);
                          useSceneStore.getState().selectSubObject(null);
                        }
                      }}
                      title={unlocked ? '已解锁：可展开构件、选择部件编辑（点击锁定）' : '已锁定：整体操作（点击解锁后可展开构件）'}
                    />
                  ) : null,
                  subCount > 0 ? (
                    <Button
                      type="text" size="small" icon={<ApartmentOutlined />}
                      disabled={!unlocked}
                      onClick={(e) => { e.stopPropagation(); openModelTree(model.id); }}
                      title={unlocked ? `${subCount} 个构件（组合模型自动解析）` : '解锁后可展开构件'}
                    />
                  ) : null,
                  <Button
                    type="text" size="small"
                    icon={model.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    onClick={() => handleToggleVisible(model.id, !model.visible)}
                  />,
                  <Popconfirm title="确认删除？" onConfirm={() => handleDelete(model.id)}>
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ].filter(Boolean)}
              >
                <Text ellipsis style={{ maxWidth: 110 }}>{model.name}</Text>
                {model.aiGenerated ? (
                  <span
                    title="AI 生成/修改"
                    style={{ fontSize: 10, color: '#9254de', border: '1px solid #9254de', borderRadius: 3, padding: '0 3px', marginLeft: 4 }}
                  >
                    AI
                  </span>
                ) : null}
                {subCount > 0 && <Text type="secondary" style={{ fontSize: 11 }}>{subCount}构件</Text>}
              </List.Item>
              {unlocked && expandedModelId === model.id && subCount > 0 && expandedTree && (
                <div style={{ padding: '4px 8px 8px 20px', background: '#10101f', borderRadius: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <Text style={{ fontSize: 11, color: '#888', flex: 1 }}>构件（勾选=显示）：</Text>
                    <Input
                      size="small"
                      allowClear
                      prefix={<SearchOutlined style={{ fontSize: 11 }} />}
                      placeholder="搜索构件"
                      value={treeSearchText}
                      onChange={(e) => handleTreeSearch(e.target.value, model.id)}
                      style={{ width: 140, fontSize: 11 }}
                    />
                  </div>
                  <Tree
                    key={`tree-${model.id}-${treeRefreshKey}`}
                    ref={treeRef}
                    // 2026-09-14 修复：Tree 必须设 height 才启用内部滚动容器，否则 treeRef.scrollTo 无效（滚动发生在外层）
                    height={280}
                    checkable
                    onRightClick={(info) => {
                      // 2026-09-14（阶段 B）：树节点右键 → 上下文菜单（与视口右键共用）
                      // 2026-09-14 修复：右键"已在选中集合内"的节点时保持多选（否则多选被清成单选，
                      // 菜单操作只作用于单个——用户"选中 24 个右键删除无效果"根因）
                      const uuid = String(info.node.key);
                      const tree = subObjectsMap[model.id] || [];
                      const node = collectNodes(tree).find((n) => n.uuid === uuid);
                      const st = useSceneStore.getState();
                      const inSelection = st.selectedSubObjects.some((x) => x.modelId === model.id && x.uuid === uuid);
                      if (!inSelection) {
                        setTreeSelectedKeys([uuid]);
                        selectModel(model.id);
                        st.selectSubObject({ modelId: model.id, uuid });
                      }
                      useSubObjectMenuStore.getState().openMenu({
                        x: (info.event as any)?.clientX ?? 400,
                        y: (info.event as any)?.clientY ?? 300,
                        modelId: model.id,
                        uuid,
                        name: node?.name || '部件',
                      });
                    }}
                    expandedKeys={treeExpandedKeys}
                    onExpand={(keys) => setTreeExpandedKeys(keys)}
                    defaultCheckedKeys={expandedTree.checkedKeys}
                    treeData={expandedTree.treeData}
                    selectedKeys={treeSelectedKeys}
                    selectable
                    multiple
                    onSelect={(keys, e) => {
                      // 2026-09-08｜修复：点击树节点 = 选中该构件（3D 同步选中/属性面板跟随）；勾选框仍为显隐
                      // 2026-09-14：multiple 多选（Ctrl/⌘ 追加；普通点击替换）——多选同步到部件多选状态
                      if (keys.length === 0) {
                        setTreeSelectedKeys([]);
                        useSceneStore.getState().selectSubObjects([]);
                        return;
                      }
                      const list = keys.map((k) => ({ modelId: model.id, uuid: String(k) }));
                      setTreeSelectedKeys(keys);
                      selectModel(model.id);
                      useSceneStore.getState().selectSubObjects(list);
                      // 单击（选中态变化）聚焦主选中（末位）部件
                      if (e?.selected) {
                        setTimeout(() => cameraControlsRegistry.focusSelected?.(), 80);
                      }
                    }}
                    onCheck={(keys, e) => applyVisibility(model.id, keys)}
                  />
                </div>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
